
/**
 * Turns the server's pack config into the exact percentages the Card Packs
 * header prints. Pool eligibility and collection-weighted probabilities are
 * published by the server; this module formats that disclosure.
 */
let CardPacksOdds = {};

CardPacksOdds = (function () {
  // Enough headroom that a real chance always survives rounding; percentages
  // this small never occur in practice, but printing "0%" for a prize that can
  // drop would be an affirmatively false disclosure.
  const MAX_FRACTION_DIGITS = 8;

  function clampFraction(value) {
    const number = Number(value);
    if (!Number.isFinite(number)) return 0;
    return Math.min(1, Math.max(0, number));
  }

  function baseFractionDigits(percent) {
    if (percent >= 10) return 0;
    if (percent >= 1) return 1;
    return 2;
  }

  function print(fraction, locale, digits) {
    try {
      return new Intl.NumberFormat(locale || 'en', {
        style: 'percent',
        maximumFractionDigits: digits,
      }).format(fraction);
    } catch (err) {
      // An unsupported locale tag must not cost the player the number itself.
      return `${Number((fraction * 100).toFixed(digits))}%`;
    }
  }

  /** Decimals this chance needs before it stops reading as zero. */
  function requiredDigits(fraction, locale) {
    const value = clampFraction(fraction);
    if (value === 0) return 0;
    for (let digits = baseFractionDigits(value * 100); digits <= MAX_FRACTION_DIGITS; digits += 1) {
      if (/[1-9]/.test(print(value, locale, digits))) return digits;
    }
    return MAX_FRACTION_DIGITS;
  }

  /**
   * Percent string for display. Round values print clean ("2%"), and a nonzero
   * chance keeps widening its precision until it stops reading as zero.
   */
  function formatPercent(fraction, locale, digits) {
    const value = clampFraction(fraction);
    const places = Number.isInteger(digits) ? digits : requiredDigits(value, locale);
    return print(value, locale, places);
  }

  function chanceOfAtLeastOne(perCardOdds, packSize) {
    const odds = clampFraction(perCardOdds);
    const size = Math.max(0, Math.floor(Number(packSize) || 0));
    if (size === 0 || odds === 0) return 0;
    return 1 - (1 - odds) ** size;
  }

  /** Every number the disclosure prints, from one authoritative config.
   * @param {{packSize?: number, stellarOdds?: number, stellarPackOdds?: number, locale?: string}} [config]
   */
  function describe({ packSize, stellarOdds, stellarPackOdds, locale } = {}) {
    if (stellarPackOdds !== undefined
      && (typeof stellarPackOdds !== 'number' || !Number.isFinite(stellarPackOdds) || stellarPackOdds < 0 || stellarPackOdds > 1)) return null;
    const stellar = clampFraction(stellarOdds);
    const normal = 1 - stellar;
    // One collection is chosen for the whole pack. Its correlated chance is
    // authoritative; the older response shape had no separate pack field.
    const atLeastOne = stellarPackOdds === undefined ? chanceOfAtLeastOne(stellar, packSize) : stellarPackOdds;
    // Stellar and Normal are complements, so they must print at the SAME
    // precision — a self-scaled complement rounds to "100%"/"0%" and the
    // published pair stops adding up. Either side can be the demanding one:
    // 0.2% Stellar needs Stellar's precision, 99.9% Stellar needs Normal's.
    const pairDigits = Math.max(
      requiredDigits(stellar, locale),
      requiredDigits(normal, locale),
    );

    return {
      stellar,
      normal,
      atLeastOne,
      canDropStellar: stellar > 0,
      stellarText: formatPercent(stellar, locale, pairDigits),
      normalText: formatPercent(normal, locale, pairDigits),
      atLeastOneText: formatPercent(atLeastOne, locale),
    };
  }

  return {
    formatPercent,
    chanceOfAtLeastOne,
    describe,
  };
})();

export { CardPacksOdds };
